import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, auth, inputCls, Field, Modal, PageHead, PrimaryBtn, errMsg, fmtDate } from './finance/shared';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';

const Stars = ({ score }) => (
  <span className="inline-flex items-center gap-0.5" dir="ltr">
    {[1, 2, 3, 4, 5].map((i) => <Icon key={i} name="star" className={`w-3.5 h-3.5 ${i <= Math.round(score) ? 'text-amber-400' : 'text-slate-200'}`} />)}
    <span className="mr-1 text-xs font-bold text-slate-600">{score}</span>
  </span>
);

export default function PerformanceReviewPage() {
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empFilter, setEmpFilter] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [toast, setToast] = useState(null);
  const notify = (m) => { setToast(m); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    const g = (u) => axios.get(`${API}${u}`, auth()).then((r) => r.data).catch(() => []);
    const [r, e] = await Promise.all([g('/performance-reviews'), g('/employees')]);
    setReviews(Array.isArray(r) ? r : []);
    setEmployees(Array.isArray(e) ? e.filter((x) => x.is_active) : []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openForm = (r) => {
    setEditing(r || null);
    setForm(r ? { ...r } : { employee_id: '', period: '', score: 3, strengths: '', improvements: '', notes: '' });
    setErr(null); setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (!editing && !form.employee_id) { setErr('اختر الموظف'); return; }
    if (!form.period?.trim()) { setErr('فترة التقييم مطلوبة (مثال: الربع الثاني 2026)'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/performance-reviews/${editing.id}`, { period: form.period, score: Number(form.score), strengths: form.strengths || null, improvements: form.improvements || null, notes: form.notes || null }, auth());
      } else {
        await axios.post(`${API}/performance-reviews`, { employee_id: form.employee_id, period: form.period.trim(), score: Number(form.score), strengths: form.strengths || null, improvements: form.improvements || null, notes: form.notes || null }, auth());
      }
      setModal(false); notify('تم حفظ التقييم'); load();
    } catch (e2) { setErr(errMsg(e2, 'فشل الحفظ')); }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/performance-reviews/${confirmDel.id}`, auth()); notify('تم حذف التقييم'); setConfirmDel(null); load(); }
    catch (e2) { notify(errMsg(e2, 'تعذر الحذف')); setConfirmDel(null); }
  };

  const visible = reviews.filter((r) => !empFilter || r.employee_id === empFilter);
  const avg = visible.length ? (visible.reduce((s, r) => s + (r.score || 0), 0) / visible.length).toFixed(1) : '—';

  return (
    <div className="mx-auto max-w-6xl space-y-4" data-testid="performance-page">
      {toast && <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-[#16233b] px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}
      <PageHead title="تقييم الأداء" desc="تقييمات دورية لأداء موظفي المنشأة"
        action={<PrimaryBtn onClick={() => openForm(null)} data-testid="review-add-btn"><Icon name="plus" className="w-4 h-4" /> تقييم جديد</PrimaryBtn>} />

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">إجمالي التقييمات</div><div className="text-xl font-bold text-slate-900" data-testid="reviews-total">{reviews.length}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">متوسط التقييم</div><div className="text-xl font-bold text-teal-700">{avg}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">الموظفون النشطون</div><div className="text-xl font-bold text-slate-900">{employees.length}</div></div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
          <select value={empFilter} onChange={(e) => setEmpFilter(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm" data-testid="review-emp-filter">
            <option value="">كل الموظفين</option>
            {employees.map((e2) => <option key={e2.id} value={e2.id}>{e2.full_name}</option>)}
          </select>
          <span className="mr-auto text-xs text-slate-400">{visible.length} تقييم</span>
        </div>
        {loading ? <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-lg bg-slate-100" />)}</div>
          : visible.length === 0 ? <div className="p-6"><EmptyState icon="chart" text="لا توجد تقييمات بعد — ابدأ بتقييم أول موظف" testId="reviews-empty" /></div>
          : (
            <div className="divide-y divide-slate-50">
              {visible.map((r) => (
                <div key={r.id} className="px-4 py-3" data-testid="review-row">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="text-sm font-bold text-slate-900">{r.employee_name}</span>
                      <span className="mr-2 text-xs text-slate-400">{r.employee_position}</span>
                      <span className="mr-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">{r.period}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Stars score={r.score} />
                      <button onClick={() => openForm(r)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-teal-700" data-testid="review-edit-btn"><Icon name="cog" className="w-4 h-4" /></button>
                      <button onClick={() => setConfirmDel(r)} className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid="review-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {(r.strengths || r.improvements || r.notes) && (
                    <div className="mt-1.5 grid grid-cols-1 gap-1 text-xs text-slate-500 sm:grid-cols-3">
                      {r.strengths && <div><span className="font-semibold text-teal-700">نقاط القوة:</span> {r.strengths}</div>}
                      {r.improvements && <div><span className="font-semibold text-amber-700">فرص التحسين:</span> {r.improvements}</div>}
                      {r.notes && <div><span className="font-semibold text-slate-600">ملاحظات:</span> {r.notes}</div>}
                    </div>
                  )}
                  <div className="mt-1 text-[10px] text-slate-400">قيّمه {r.reviewer} — {fmtDate(r.review_date)}</div>
                </div>
              ))}
            </div>
          )}
      </div>

      <Modal open={modal} title={editing ? 'تعديل التقييم' : 'تقييم جديد'} onClose={() => setModal(false)} wide>
        {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="review-form-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          {!editing && (
            <Field label="الموظف" required>
              <select className={inputCls} value={form.employee_id || ''} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="review-input-employee">
                <option value="">اختر الموظف</option>
                {employees.map((e2) => <option key={e2.id} value={e2.id}>{e2.full_name} — {e2.position}</option>)}
              </select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="فترة التقييم" required><input className={inputCls} placeholder="مثال: الربع الثاني 2026" value={form.period || ''} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="review-input-period" /></Field>
            <Field label={`الدرجة (${form.score || 3} من 5)`}>
              <input type="range" min="1" max="5" step="0.5" value={form.score || 3} onChange={(e) => setForm({ ...form, score: e.target.value })} className="w-full accent-[#16233b]" data-testid="review-input-score" />
            </Field>
          </div>
          <Field label="نقاط القوة"><input className={inputCls} value={form.strengths || ''} onChange={(e) => setForm({ ...form, strengths: e.target.value })} /></Field>
          <Field label="فرص التحسين"><input className={inputCls} value={form.improvements || ''} onChange={(e) => setForm({ ...form, improvements: e.target.value })} /></Field>
          <Field label="ملاحظات"><input className={inputCls} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="review-form-submit">حفظ التقييم</PrimaryBtn>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(confirmDel)} title="حذف التقييم" onClose={() => setConfirmDel(null)}>
        <p className="text-sm text-slate-600">حذف تقييم «{confirmDel?.employee_name}» لفترة {confirmDel?.period}؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmDel(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={remove} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="review-confirm-del">حذف</button>
        </div>
      </Modal>
    </div>
  );
}
